import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { chunk, cloneDeep, concat, find, findKey, get, map, isEqual } from 'lodash'
import apiMenu from '@/api/menu'
import Cookies from 'js-cookie'

export default function ({
  menuType,
  firstDeliveryDate,
  dayTime,
  seenFiltersTutorial,
  needToShowFiltersTutorial,
  needToShowFiltersDateTutorial,
  tryToShowFiltersTutorial,
  isLoadingMenu,
  useFiltersLogic,
  tryToShowFiltersTutorialOnScroll,
  currentPricePlan,
  mealDaysData,
  setWithoutFilters,
  getMenuRequestParams,
}) {
  // data
  /**
   * @description настройки для расчетов пенальти
   */
  const config = ref({})

  /**
   * @description Не обработанное продуктовое меню
   */
  const foodMenu = ref(null)

  /**
   * @description Обработанное продуктовое меню
   */
  const foodMenuAndReplacement = ref(null)

  /**
   * @description кастомизация с бэка, не изменяемая пользователем (избранное/стоп лист)
   */
  const customPacksFavorite = ref({})

  /**
   * @description Кастомизация клиента
   */
  const customPacks = ref({})

  /**
   * @description Изменения относительно стандартной цены
   */
  const diffMenuPrice = ref({})

  /**
   * @description Содержит дату первого дня с возможной кастомизацией
   */
  const firstDayWithCustom = ref(null)

  /**
   * @description Список ошибок при загрузке
   */
  const errorsList = ref([])
  const setErrorsList = (val) => {
    errorsList.value = val
  }

  /**
   * @description Переменная показывает загружался ли контент
   */
  const isLoaded = ref(false)

  // computed
  const store = useStore()
  // getters
  const getDeliveryDaysList = computed(
    () => store.getters['customizationInfo/deliveryDays'],
  )
  // from index
  const getCurrentMenuDeliveryDates = computed(() => {
    if (!getDeliveryDaysList.value || getDeliveryDaysList.value.length < 1) {
      return null
    }
    return getDeliveryDaysList.value.find(
      (value) => value.menuType === menuType.value,
    )
  })

  /**
   * @description Кастом конфиг текущего меню (формируется при кастомизации)
   * @return []
   */
  const getCurrentCustomizationConfig = computed(
    () => customPacks.value[menuType.value] || [],
  )

  /**
   * @description Добавочная цена к заказу (формируется при кастомизации)
   * @return {number}
   */
  const getCurrentMenuDiffPrice = computed(() => {
    return diffMenuPrice.value[menuType.value] || 0
  })
  /**
   * @description Продуктовое меню выбранного дня
   */
  const deliveryPacksDefault = computed(() => {
    /**
     * Формируем полное меню на выбранную доставку
     * */
    // TODO: ahtung! privet localy na 7 dnei
    // ili do togo momenta, kak mojno bydet vibrat' vecher ili ytro
    if (foodMenu.value) {
      const chunkDays = chunk(
        Object.values(get(foodMenu.value, 'days')),
        foodMenu.value.config.numberOfDaysInDelivery,
      )
      return chunkDays
    }
    return []
  })

  const deliveryPacks = computed(() => {
    if (foodMenuAndReplacement.value) {
      const chunkDays = chunk(
        Object.values(get(foodMenuAndReplacement.value, 'days')),
        foodMenuAndReplacement.value.config.numberOfDaysInDelivery,
      )

      return chunkDays
    }
    return []
  })

  const deliveryPrice = computed(() => {
    let paidDeliveries = 0
    deliveryPacks.value.forEach((delivery) => {
      const totalPacksPerDelivery = delivery.reduce((summ, value) => {
        const count = value.items.reduce((mealSumm, meal) => {
          return mealSumm + meal.onDesck?.length || 0
        }, 0)
        return summ + count
      }, 0)
      const minimumMealsPerDelivery =
        delivery[0]?.config?.minimumMealsPerDelivery || 0
      if (
        minimumMealsPerDelivery > 1 &&
        totalPacksPerDelivery < minimumMealsPerDelivery
      ) {
        // paidDeliveries += 1
        const deliveryPenaltyPrice =
          delivery[0]?.config?.deliveryPenaltyPrice || 0
        paidDeliveries = paidDeliveries + deliveryPenaltyPrice
      }
    })
    return paidDeliveries
  })

  // methods
  /**
   * @description Устанавливаем продуктовое меню и запускаем его обработку
   */
  const setFoodMenuAndReplacementPacks = (data, isFiltersEnabled = false) => {
    foodMenuAndReplacement.value = data

    if (!customPacks.value?.[menuType.value]) {
      customPacks.value[menuType.value] = data.customization
    } else {
      const currentCustomization = isFiltersEnabled
        ? []
        : customPacks.value[menuType.value] || []
      const newCustomization = data.customization || []
      customPacks.value[menuType.value] =
        currentCustomization.concat(newCustomization)
    }

    customPacksFavorite.value[menuType.value] = data.customization.filter(
      (customizationAction) => {
        return customizationAction.isAutoCustom
      },
    )

    config.value = data.config

    // Первый этап - открываем major и secondary паки (замена ID на пак)
    setOpenedPackage()

    // Второй этап - если есть кастом, то устанавливаем кастом в меню
    setCustomizationByPacks()

    // Третий этап - собираем отображаемое меню (onDesck)
    setOnDesck()
  }

  /**
   * @description Раскрываем паки внутри меню (паки приходят в формате id, заменяем id на объект пака)
   */
  const setOpenedPackage = () => {
    const orderMenuDays = cloneDeep(foodMenuAndReplacement.value.days)

    map(orderMenuDays, (day, date) => {
      return map(day.items, (item, index) => {
        item.major = map(item.major, (packIdMajor) => {
          const majorPack = foodMenuAndReplacement.value.packs.find(
            (pack) => pack.id === packIdMajor,
          )

          return {
            ...majorPack,
            ...{
              amount: 1,
              changed: false,
            },
          }
        })

        item.secondary = map(item.secondary, (packIdSecondary) => {
          const secondaryPack = foodMenuAndReplacement.value.packs.find(
            (pack) => pack.id === packIdSecondary,
          )

          return {
            ...secondaryPack,
            ...{
              amount: 0,
              changed: false,
            },
          }
        })

        orderMenuDays[date].items[index] = item
      })
    })

    foodMenuAndReplacement.value.days = orderMenuDays
  }

  /**
   * @description Накладываем всю имеющуюся кастомизацию
   * @description this.customPacksFavorite - кастомизация с бэка, не изменяемая пользователем (избранное/стоп лист)
   * @description this.customPacks - кастомизация пользователя
   * */
  const setCustomizationByPacks = () => {
    const orderMenuDays = cloneDeep(foodMenuAndReplacement.value.days)

    console.group('Кастомизация:')

    const additionalPackList = concat(
      customPacksFavorite.value[menuType.value] || [],
      customPacks.value[menuType.value] || [],
    )

    setDiffMenuPrice({
      menu_type: menuType.value,
      value: 0,
      type: 'reset',
    })

    map(additionalPackList, (pack, index) => {
      if (!pack) {
        return
      }

      // Отфильтруем меню по искомому варианту замены
      if (!get(pack, 'mealDate')) {
        console.error('pack', pack)
      }

      const dayPacks = get(
        find(orderMenuDays, {
          date: pack.mealDate,
        }),
        'items',
      )

      // Возьмем только нужный mealNumber
      const packsInPosition = find(dayPacks, {
        position: pack.mealNumber,
      })

      if (!packsInPosition) {
        /**
         * Что-то не так с датами и конфигом, поэтому все сбросим и оповестим нотификацией
         * */
        console.error(
          `${index}. Ошибка поиска пака ${pack.packId} на ${pack.mealDate}, прием ${pack.mealNumber}`,
        )
        return
      }

      const isMajorPack = findKey(packsInPosition.major, {
        id: pack.packId,
      })
      const isSecondaryPack = findKey(packsInPosition.secondary, {
        id: pack.packId,
      })

      const pos = {}

      if (isMajorPack) {
        pos.type = 'major'
        pos.index = isMajorPack
      } else if (isSecondaryPack) {
        pos.type = 'secondary'
        pos.index = isSecondaryPack
      } else {
        console.error(
          `${index}. Пак ${pack.packId} на ${pack.mealDate}, прием ${pack.mealNumber} НЕ НАЙДЕН`,
        )
        return
      }

      const currentDay = orderMenuDays.find(
        (value) => value.date === pack.mealDate,
      )

      const currentDayItems = currentDay.items

      const currentMeal = currentDayItems.find(
        (value) => value.position === pack.mealNumber,
      )
      const currentType = currentMeal[pos.type]
      const currentPack = currentType[pos.index]

      const currentPackRealPrice =
        currentDayItems
          .find((value) => value.position === pack.mealNumber)
          ?.prices?.find((value) => value.packId === pack.packId)?.price || 0

      if (pack.remove) {
        currentDay.items.find((item) => item.position === pack.mealNumber)[
          pos.type
        ][pos.index].amount = +currentPack.amount - 1

        if (!pack.isAutoCustom) {
          setDiffMenuPrice({
            menu_type: menuType.value,
            value: -currentPackRealPrice,
            type: 'additional',
          })
        }
      } else if (!pack.remove) {
        currentDay.items.find((item) => item.position === pack.mealNumber)[
          pos.type
        ][pos.index].changed = true

        currentDay.items.find((item) => item.position === pack.mealNumber)[
          pos.type
        ][pos.index].amount = +currentPack.amount + 1

        if (!pack.isAutoCustom) {
          setDiffMenuPrice({
            menu_type: menuType.value,
            value: currentPackRealPrice,
            type: 'additional',
          })
        }
      }
    })
    console.groupEnd()
    foodMenuAndReplacement.value.days = orderMenuDays
  }

  /**
   * @description устанавливаем onDesck (То что будет отображаться на "столе")
   */
  const setOnDesck = () => {
    if (foodMenuAndReplacement?.value?.days) {
      foodMenuAndReplacement.value.days.forEach((day) => {
        day.items.forEach((item) => {
          const currentDayData = []
          item.major.forEach((updPack) => {
            if (updPack.amount <= 0) {
              return
            }

            const packCopy = cloneDeep(updPack)

            const { price = null } =
              item.prices.find((value) => value.packId === updPack.id) || {}

            packCopy.price = price
            packCopy.amount = 1

            currentDayData.push(packCopy)
          })

          item.secondary.forEach((updPack) => {
            if (updPack.amount <= 0) {
              return
            }

            const packCopy = cloneDeep(updPack)

            const { price = null } =
              item.prices.find((value) => value.packId === updPack.id) || {}

            packCopy.price = price
            packCopy.amount = 1

            currentDayData.push(packCopy)
          })

          item.onDesck = currentDayData
        })
      })
    }
  }

  const setDiffMenuPrice = (data) => {
    const value = Number(data.value) || 0

    if (data.type === 'additional') {
      const oldValue = diffMenuPrice.value[data.menu_type] || 0
      diffMenuPrice.value[data.menu_type] = +oldValue + +value
    } else {
      diffMenuPrice.value[data.menu_type] = value
    }
  }

  /**
   * @description Удалить пак из текущего конфига, если он уже был добавить конфиг с удалением текущего пака
   */
  const removeFromCustomConfig = (pack) => {
    pack.remove = true
    addToCustomConfig(pack)
    resetMenuDiffPrice()
  }

  /**
   * @description Высчитываем стоимость кастома
   */
  const resetMenuDiffPrice = () => {
    setDiffMenuPrice({
      menu_type: menuType.value,
      value: 0,
      type: 'reset',
    })

    const additionalPackList = customPacks.value[menuType.value]
    const daysDeliveryPacksCounter = deliveryPacksDefault.value.map(
      (delivery) => {
        return {
          dates: delivery.map((value) => value.date),
          config: delivery[0].config,
          defaultItemsCount: delivery.reduce((summ, value) => {
            const count = value.items.reduce((mealSumm, meal) => {
              return mealSumm + meal?.onDesck.length || 0
            }, 0)
            return summ + count
          }, 0),
          count: 0,
        }
      },
    )
    additionalPackList.forEach((pack) => {
      if (!pack) {
        return
      }

      const currentDeliveryCounter = daysDeliveryPacksCounter.find((day) => {
        return day.dates.includes(pack.mealDate)
      }) || { count: 0 }

      const currentDay = foodMenuAndReplacement.value.days.find(
        (value) => value.date === pack.mealDate,
      )
      const currentDayItems = currentDay?.items || []
      const currentPackRealPrice =
        currentDayItems
          .find((item) => item.position === pack.mealNumber)
          ?.prices?.find((value) => value.packId === pack.packId)?.price || 0

      // daysDeliveryPacksCounter
      if (pack.remove) {
        currentDeliveryCounter.count--
        setDiffMenuPrice({
          menu_type: menuType.value,
          value: -currentPackRealPrice,
          type: 'additional',
        })
      } else {
        currentDeliveryCounter.count++
        setDiffMenuPrice({
          menu_type: menuType.value,
          value: currentPackRealPrice,
          type: 'additional',
        })
      }
    })
    daysDeliveryPacksCounter.forEach((delivery) => {
      if (delivery.config.minimumMealsPerDelivery === 1) {
        const minPackCountForPaidRemove =
          delivery.config.minPackCountForPaidRemove > delivery.defaultItemsCount
            ? delivery.defaultItemsCount
            : delivery.config.minPackCountForPaidRemove
        if (
          delivery.defaultItemsCount + delivery.count <=
          minPackCountForPaidRemove
        ) {
          setDiffMenuPrice({
            menu_type: menuType.value,
            value:
              delivery.config.packRemovePrice *
              (minPackCountForPaidRemove -
                (delivery.defaultItemsCount + delivery.count)),
            type: 'additional',
          })
        }
      }
    })
  }
  const applyTemplate = (mealNumbersToRemove) => {
    if (mealNumbersToRemove) {
      const days = foodMenuAndReplacement.value?.days || []
      if (mealNumbersToRemove.length > 0) {
        days.forEach((value) => {
          if (value.customizable) {
            value.items.forEach((item) => {
              if (mealNumbersToRemove.includes(item.position)) {
                item.onDesck.forEach((pack, index) => {
                  removePackFromMenuAndCustomConfig({
                    oldPack: {
                      mealDate: value.date,
                      mealNumber: item.position,
                      packId: pack.id,
                      packIndex: index,
                    },
                    type: 'remove',
                  })
                })
              }
            })
          }
        })
      } else {
        days.forEach((value) => {
          if (value.customizable) {
            value.items.forEach((item) => {
              if (item.onDesck.length === 0) {
                item.major.forEach((pack) => {
                  updateMenuAndCustomConfig({
                    action: 'add',
                    newPack: {
                      mealDate: value.date,
                      mealNumber: item.position,
                      packId: pack.id,
                      packIndex: 0,
                      remove: false,
                    },
                    currentPack: pack,
                    oldPack: {
                      mealDate: value.date,
                      mealNumber: item.position,
                      packId: pack.id,
                      packIndex: 0,
                      remove: false,
                    },
                  })
                })
              }
            })
          }
        })
      }
    } else {
      resetMenuDiffPrice()
    }
  }
  /**
   * @description Добавить Удаленный пак текущего конфига кастома
   */
  const deleteFromCustomConfig = (pack) => {
    let customPacksLocal = []

    if (
      Object.prototype.hasOwnProperty.call(customPacks.value, menuType.value)
    ) {
      customPacksLocal = customPacks.value[menuType.value]
    }
    const index = customPacksLocal.findIndex(
      ({ packId, mealNumber, mealDate }) => {
        return (
          packId === pack.packId &&
          mealNumber === pack.mealNumber &&
          mealDate === pack.mealDate
        )
      },
    )
    customPacksLocal.splice(index, 1)

    customPacks.value[menuType.value] = customPacksLocal
    setOnDesck()
    resetMenuDiffPrice()
  }

  const resetTemplate = (mealNumbersToRemove) => {
    if (mealNumbersToRemove) {
      const days = foodMenuAndReplacement.value?.days || []
      if (mealNumbersToRemove.length > 0) {
        days.forEach((value) => {
          if (value.customizable) {
            value.items.forEach((item) => {
              if (mealNumbersToRemove.includes(item.position)) {
                item.major.forEach((pack, index) => {
                  deleteFromCustomConfig({
                    mealDate: value.date,
                    mealNumber: item.position,
                    packId: pack.id,
                    packIndex: index,
                  })
                })
              }
            })
          }
        })
      } else {
      }
    } else {
      customPacks.value[menuType.value] = []
      setOnDesck()
      resetMenuDiffPrice()
    }
  }
  const removePackFromMenuAndCustomConfig = (data) => {
    const { oldPack = null } = data

    // TODO: refactor customization
    // при замене пака из onDesck должен удалится заменяемый пак и добавиться заменяющий, здесь добавляется заменяемый
    const currentDay = foodMenuAndReplacement.value.days.find(
      (value) => value.date === oldPack.mealDate,
    )
    const pcks = currentDay.items.find(
      (value) => value.position === oldPack.mealNumber,
    ).onDesck

    pcks.splice(oldPack.packIndex, 1)

    removeFromCustomConfig(oldPack)
  }

  /**
   * @description Добавить новый пак в текущий конфиг кастома
   */
  const addToCustomConfig = (newPack) => {
    let customPacksLocal = []

    if (
      Object.prototype.hasOwnProperty.call(customPacks.value, menuType.value)
    ) {
      customPacksLocal = customPacks.value[menuType.value]
    }

    customPacksLocal.push(newPack)

    customPacks.value[menuType.value] = customPacksLocal
    resetMenuDiffPrice()
  }

  const updateMenuAndCustomConfig = (data) => {
    const {
      action = '',
      oldPack = null,
      newPack = null,
      currentPack = null,
    } = data

    // TODO: refactor customization
    // при замене пака из onDesck должен удалится заменяемый пак и добавиться заменяющий, здесь добавляется заменяемый
    const currentDay = foodMenuAndReplacement.value.days.find(
      (value) => value.date === oldPack.mealDate,
    )
    const pcks = currentDay.items.find(
      (value) => value.position === oldPack.mealNumber,
    ).onDesck

    if (oldPack.packId && action === 'change') {
      removePackFromMenuAndCustomConfig(data)
    }

    pcks.splice(newPack.packIndex, 0, { ...currentPack, changed: true })
    addToCustomConfig(newPack)
  }

  const getCustomizationInfoMenu = async ({
    filterIds = null,
    selectedDate,
    menuRequestParams = {},
    ignorePreloadedMenu = false,
  }) => {
    if (!menuRequestParams.menuType) {
      return
    }

    const preloadedMenus = store.getters['client/preloadedMenus']
    const preloadedMenu = cloneDeep(preloadedMenus?.[menuRequestParams.menuType])
    if (preloadedMenu?.loading && !ignorePreloadedMenu) {
      return
    }
    const bftucid = menuRequestParams.bftucid || Cookies.get('bftucid')
    isLoadingMenu.value = true
    const appliedUnauthorizedFilters = JSON.parse(window.localStorage.getItem('appliedUnauthorizedFilters')) || []
    const filterIdsStore = appliedUnauthorizedFilters.map((item) => item.id)
    let response = null
    if ((!filterIds || isEqual(filterIds, filterIdsStore)) && preloadedMenu && !ignorePreloadedMenu) {
      const config = preloadedMenu?.config?.data ? JSON.parse(preloadedMenu?.config?.data) : null
      const data = preloadedMenu?.data?.data
      const configFilterIds = config.filterIds || []
      if (
        config?.cityId === menuRequestParams.cityId &&
        config?.menuDays === menuRequestParams.menuDays &&
        data?.dayTime === menuRequestParams.dayTime &&
        data?.firstDeliveryDate === menuRequestParams.firstDeliveryDate &&
        isEqual(filterIdsStore, configFilterIds)
      ) {
        response = cloneDeep(preloadedMenu)
      }
    }
    let updateRequest = false // флаг показывающий, что нужно ли обновить данные в сторадже
    if (!response) {
      // что бы не было запросов пока идет текущий записываем тип меню в сторадже
      store.dispatch('client/updatePreloadedMenus', {
        [menuRequestParams.menuType]: { loading: true },
      })
      try {
        response = await apiMenu.postCustomizationInfoMenu({ ...menuRequestParams, bftucid })
        updateRequest = true // показываем, что нужно ли обновить данные в сторадже
      } catch (error) {
        response = null
      }
    }

    // логика туториалов
    if (useFiltersLogic) {
      if (
        (menuRequestParams?.filterIds?.length ||
          filterIds?.length) &&
        !seenFiltersTutorial.value
      ) {
        needToShowFiltersTutorial.value = true
      } else {
        needToShowFiltersTutorial.value = false
      }
      if (
        (menuRequestParams?.filterIds?.length ||
          filterIds?.length) &&
        (!firstDeliveryDate.value ||
          firstDeliveryDate.value !== response?.data?.data?.firstDeliveryDate)
      ) {
        needToShowFiltersDateTutorial.value = true
      } else {
        needToShowFiltersDateTutorial.value = false
      }
    }
    // /логика туториалов

    if (response) {
      isLoaded.value = true
      const { data: { data = null, success = false, messages = [] } = {} } =
        cloneDeep(response)
      firstDayWithCustom.value = null
      if (success) {
        // если два запроса отправились одновременно то записать паки из выбранного
        if (menuType.value === menuRequestParams.menuType) {
          setFoodMenuAndReplacementPacks(data, filterIds?.length || menuRequestParams?.filterIds?.length)
        }
        // запуск туториалов после обновления меню
        if (useFiltersLogic) {
          const currentDay = foodMenuAndReplacement.value.days.find(
            (value) => value.date === selectedDate,
          ) || { customizable: true }
          if (!currentDay?.customizable && needToShowFiltersTutorial.value && config.value.canUseFilters) {
            tryToShowFiltersTutorial('customDayFilters')
          }
          if (needToShowFiltersDateTutorial.value && config.value.canUseFilters) {
            tryToShowFiltersTutorial('shiftedFirstDeliveryDateTutorial')
          }
        }
      }

      if (!firstDayWithCustom.value) {
        const firstCustomDate = foodMenuAndReplacement.value.days.find(
          (value) => value.customizable,
        )?.date
        if (firstCustomDate) {
          firstDayWithCustom.value = firstCustomDate
        }
        if (!firstDayWithCustom.value) {
          firstDayWithCustom.value =
            foodMenuAndReplacement.value?.days?.[0]?.date || null
        }
      } else {
        foodMenuAndReplacement.value = data
        errorsList.value = messages
        messages.forEach((message) => {
          console.log(message)
        })
      }

      if (useFiltersLogic) {
        dayTime.value = data?.dayTime
        firstDeliveryDate.value = data?.firstDeliveryDate
      }

      foodMenu.value = cloneDeep(data)

      isLoadingMenu.value = false

      store.dispatch('analytics/firebasePerformanceCustomTraceStop', 'menuLoading')
      store.dispatch('analytics/firebasePerformanceCustomTraceStop', 'menuChanging')

      if (tryToShowFiltersTutorialOnScroll) {
        tryToShowFiltersTutorialOnScroll()
      }

      if (updateRequest) {
        // если данные из запроса, то записать их в reloadedMenus
        store.dispatch('client/updatePreloadedMenus', {
          [menuRequestParams.menuType]: response,
        })
      }

      return data
    }

    return null
  }

  const resetCustomization = () => {
    customPacks.value[menuType.value] = []
    resetMenuDiffPrice()
    setWithoutFilters(true)
    window.localStorage.removeItem('appliedUnauthorizedFilters')
    if (store.getters['client/isLoggedIn']) {
      store.dispatch('customizationInfo/setFilters', [])
    }
  }

  const customDaysPoisition = computed(() => {
    return foodMenuAndReplacement.value?.days.findIndex(
      ({ customizable }) => !customizable,
    )
  })
  const hasNoCustomDays = computed(() => {
    return customDaysPoisition.value >= 0
  })

  const customizedMealsCount = computed(() => {
    if (
      !customPacks.value[menuType.value] ||
      !customPacks.value[menuType.value].length === 0
    ) { return 0 }
    const mealsSet = new Set()
    for (const i in customPacks.value[menuType.value]) {
      if (customPacks.value[menuType.value][i]) {
        mealsSet.add(
          `${customPacks.value[menuType.value][i].mealDate} ${
            customPacks.value[menuType.value][i].mealNumber
          }`,
        )
      }
    }
    return mealsSet.size
  })

  const diffPriceWithDiscounts = computed(() => {
    const percentDiscount = currentPricePlan.value.mealDays
      .find((md) => {
        const daysToCompare = md.subscriptionDays || md.numberOfMealDays
        return parseInt(daysToCompare) === parseInt(mealDaysData.value?.mealDays)
      })
      ?.promoInfo?.discounts?.find(
        (discount) => discount.discountType === 'percent',
      )?.discountValue
    if (percentDiscount) {
      return Math.ceil(
        getCurrentMenuDiffPrice.value * ((100 - percentDiscount) / 100) +
          deliveryPrice.value,
      )
    } else {
      return Math.ceil(getCurrentMenuDiffPrice.value + deliveryPrice.value)
    }
  })

  return {
    // data
    isLoadingMenu,
    isLoaded,
    config,
    customPacks,
    diffMenuPrice,
    firstDayWithCustom,
    foodMenuAndReplacement,
    errorsList,
    // computed
    getDeliveryDaysList,
    getCurrentMenuDeliveryDates,
    getCurrentCustomizationConfig,
    getCurrentMenuDiffPrice,
    deliveryPacksDefault,
    deliveryPacks,
    deliveryPrice,
    customDaysPoisition,
    hasNoCustomDays,
    customizedMealsCount,
    diffPriceWithDiscounts,
    // methods
    setFoodMenuAndReplacementPacks,
    setOpenedPackage,
    setCustomizationByPacks,
    setOnDesck,
    setDiffMenuPrice,
    removeFromCustomConfig,
    resetMenuDiffPrice,
    removePackFromMenuAndCustomConfig,
    addToCustomConfig,
    updateMenuAndCustomConfig,
    getCustomizationInfoMenu,
    applyTemplate,
    resetTemplate,
    setErrorsList,
    resetCustomization,
  }
}
